import React from 'react';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import EditIcon from '@mui/icons-material/Edit';
import { Divider } from '@mui/material';

import { SelectedItemIdentifier } from '../../../../library/selection/types';
import { TransformationMenuItems } from '../../../../library/transformations/controls/TransformationMenuItems';
import { useAppMonitor } from '../../../../monitor';
import { scrollTo } from '../../../../scrolling';
import { beginCuttingCeilingPlane } from '../../../../store/actions/layout/ceilingPlaneActions';
import { handleStandardSceneItemsEditPropertiesAction } from '../../../../store/actions/sceneInteractionActions';
import { isPickingActive } from '../../../../store/selectors/pickingSelectors';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { SceneMenuContent } from '../common/SceneMenuContent';
import { SceneMenuItem } from '../common/SceneMenuItem';
import { DeleteMenuItem } from '../shared-menu-items/DeleteMenuItem';

export function CeilingPlaneMenu(props: { items: SelectedItemIdentifier[] }) {
  const monitor = useAppMonitor();
  const dispatch = useAppDispatch();

  const isPicking = useAppSelector(isPickingActive);

  const ceilingPlanes = useAppSelector((state) =>
    props.items.map((item) => state.layout.ceilingPlanes[item.guid]).filter((plane) => plane != null),
  );
  if (ceilingPlanes.length < props.items.length) throw new Error('Unable to find ceiling plane for active menu.');

  return (
    <SceneMenuContent>
      <SceneMenuItem
        text="Edit Properties"
        icon={<EditIcon />}
        onClick={async () => {
          await dispatch(handleStandardSceneItemsEditPropertiesAction('ceilingPlane', ceilingPlanes));
          monitor.recordInteraction('Ceiling Plane => Edit Properties menu item clicked');
        }}
      />

      <TransformationMenuItems />

      {ceilingPlanes.length === 1 && (
        <SceneMenuItem
          text={ceilingPlanes[0].cuts.length > 0 ? 'Re-cut plane' : 'Cut plane'}
          icon={<ContentCutIcon />}
          disabled={isPicking}
          onClick={async () => {
            scrollTo('cutPlaneButton');
            await dispatch(beginCuttingCeilingPlane(ceilingPlanes[0].guid));

            monitor.recordInteraction('Ceiling Plane => Begin Cutting menu item clicked');
          }}
        />
      )}

      <Divider />

      <DeleteMenuItem
        type="ceilingPlane"
        userVisibleTerm={ceilingPlanes.length === 1 ? 'Ceiling Plane' : 'Ceiling Planes'}
        item={props.items}
        onBeforeDelete={async (proceed) => {
          if (confirm('Are you sure you want to delete this ceiling plane?')) proceed();
        }}
      />
    </SceneMenuContent>
  );
}
